import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import {
  selectChatLockedForSession,
  selectOrbiFeedbackByMessageId,
} from '@patient-ui/patient-web/store';
import { ORBI_TOAST_SCOPE } from '@patient-ui/shared/constants';
import { OrbiFeedbackType } from '@patient-ui/shared/enums';
import { OrbiMessage } from '@patient-ui/shared/models';
import { ToastService } from '@patient-ui/shared-ui/utils';

import { LayoutService } from '../../../shared/services/layout.service';

/**
 * Response feedback + copy actions for a single agent response (PCSA-13171).
 *
 * Rendered only for TEXT llm responses (card responses are excluded — AC), as
 * the footer row INSIDE the response bubble: a copy action and thumbs-up /
 * thumbs-down. Thumbs-up / thumbs-down open the positive / negative feedback
 * BOTTOM SHEET (per Figma) via `LayoutService` — the panel hosts the sheet, so
 * this row only requests it (with the target response + sentiment). On a
 * successful submit the chosen sentiment is latched in the store (keyed by
 * messageId): the picked action stays highlighted + no longer actionable and the
 * other is disabled. Copy writes the response text to the clipboard and shows a
 * success toast (AC 1.2); on failure it surfaces a retryable inline error. The
 * whole row is hidden once the conversation is guardrail-locked (AC 12). The
 * active conversation is never interrupted.
 */
@Component({
  selector: 'patient-ui-orbi-feedback',
  templateUrl: './orbi-feedback.component.html',
  styleUrls: ['./orbi-feedback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiFeedbackComponent implements OnInit, OnDestroy {
  @Input() message!: OrbiMessage;

  readonly FeedbackType = OrbiFeedbackType;

  /** The submitted sentiment for this response, or null if none yet. */
  submitted: OrbiFeedbackType | null = null;

  /**
   * When the conversation is guardrail-locked, copy + feedback are not offered
   * on any response (AC 12); the whole row is hidden.
   */
  sessionLocked = false;

  /** Transient copy-fail flag (retryable inline error; AC 1.4). */
  copyFailed = false;

  private readonly destroyed = new Subject<void>();

  constructor(
    private readonly store: Store,
    private readonly layoutService: LayoutService,
    private readonly toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Latch the submitted sentiment from the store so the row stays locked
    // across re-renders (and if the same response re-appears).
    this.store
      .select(selectOrbiFeedbackByMessageId)
      .pipe(takeUntil(this.destroyed))
      .subscribe((map) => {
        const id = this.message?.messageId;
        this.submitted = id ? map[id] ?? null : null;
        this.cdr.markForCheck();
      });

    // Guardrail-locked conversations offer no copy/feedback actions (AC 12).
    this.store
      .select(selectChatLockedForSession)
      .pipe(takeUntil(this.destroyed))
      .subscribe((locked) => {
        this.sessionLocked = locked;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get isPositiveSubmitted(): boolean {
    return this.submitted === OrbiFeedbackType.Positive;
  }

  get isNegativeSubmitted(): boolean {
    return this.submitted === OrbiFeedbackType.Negative;
  }

  /** Actions are locked once any feedback has been submitted for this response. */
  get isLocked(): boolean {
    return this.submitted !== null;
  }

  // --- Copy -----------------------------------------------------------------
  async onCopy(): Promise<void> {
    this.copyFailed = false;
    try {
      await navigator.clipboard.writeText(this.message.text ?? '');
      // Success toast, auto-dismissing (AC 1.2) — same ToastService the feedback
      // toast uses; the active conversation is untouched (AC 1.3). Copy matches
      // the Figma "Copy response" toast exactly.
      this.toastService.show('Response copied to clipboard', {
        classname: 'border-success-dark',
        icon: 'check_circle',
        scope: ORBI_TOAST_SCOPE,
      });
    } catch {
      // No success toast; surface a retryable inline error (AC 1.4).
      this.copyFailed = true;
    }
    this.cdr.markForCheck();
  }

  // --- Open the feedback sheet ----------------------------------------------
  openPositive(): void {
    if (this.isLocked) {
      return;
    }
    this.layoutService.openOrbiFeedbackSheet({
      message: this.message,
      sentiment: OrbiFeedbackType.Positive,
    });
  }

  openNegative(): void {
    if (this.isLocked) {
      return;
    }
    this.layoutService.openOrbiFeedbackSheet({
      message: this.message,
      sentiment: OrbiFeedbackType.Negative,
    });
  }
}
